import React, { useState } from 'react';
import {
  Box, Card, CardContent, Typography, Button, TextField, Tabs, Tab,
  CircularProgress, Alert, Chip, Select, MenuItem, FormControl, InputLabel,
  IconButton,
} from '@mui/material';
import ClassificationBadge from './ClassificationBadge';
import AutoAwesomeIcon from '@mui/icons-material/AutoAwesome';
import CategoryIcon from '@mui/icons-material/Category';
import QuestionAnswerIcon from '@mui/icons-material/QuestionAnswer';
import SecurityIcon from '@mui/icons-material/Security';
import TranslateIcon from '@mui/icons-material/Translate';
import SendIcon from '@mui/icons-material/Send';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import { dmsApi } from '../services/dmsApi';
import useDmsPalette from '../hooks/useDmsPalette';

function extractKeywords(text) {
  const stopWords = new Set(['the','a','an','and','or','but','in','on','at','to','for','of','with','by','from','as','is','was','are','were','be','been','have','has','had','do','does','did','that','this','these','those','it','its']);
  const words = text.toLowerCase().match(/\b[a-z]{4,}\b/g) || [];
  const freq = {};
  words.forEach(w => { if (!stopWords.has(w)) freq[w] = (freq[w] || 0) + 1; });
  return Object.entries(freq).sort((a,b) => b[1]-a[1]).slice(0, 8).map(([w]) => w);
}

const LANGUAGES = [
  { code: 'en', label: 'English' },
  { code: 'de', label: 'German' },
  { code: 'fr', label: 'French' },
  { code: 'es', label: 'Spanish' },
  { code: 'it', label: 'Italian' },
  { code: 'pt', label: 'Portuguese' },
];


function TabPanel({ children, value, index }) {
  return value === index ? <Box sx={{ pt: 2 }}>{children}</Box> : null;
}

export default function AIPanel({ document: doc }) {
  const MP = useDmsPalette();
  const [tab, setTab] = useState(0);

  // Summary state
  const [summary, setSummary] = useState(doc?.aiSummary || null);
  const [summaryLoading, setSummaryLoading] = useState(false);
  const [summaryError, setSummaryError] = useState(null);

  // Classify state
  const [classification, setClassification] = useState(null);
  const [classifyLoading, setClassifyLoading] = useState(false);
  const [classifyError, setClassifyError] = useState(null);

  // Q&A state
  const [conversation, setConversation] = useState([]);
  const [question, setQuestion] = useState('');
  const [askLoading, setAskLoading] = useState(false);
  const [askError, setAskError] = useState(null);

  // Compliance state
  const [complianceResults, setComplianceResults] = useState(null);
  const [complianceLoading, setComplianceLoading] = useState(false);
  const [complianceError, setComplianceError] = useState(null);

  // Translate state
  const [targetLang, setTargetLang] = useState('de');
  const [translation, setTranslation] = useState(null);
  const [translateLoading, setTranslateLoading] = useState(false);
  const [translateError, setTranslateError] = useState(null);

  const handleSummarize = async () => {
    setSummaryLoading(true);
    setSummaryError(null);
    try {
      const data = await dmsApi.aiSummarize(doc._id);
      setSummary(data.summary || data.aiSummary || data);
    } catch (err) {
      setSummaryError(err.error || 'Failed to generate summary');
    } finally {
      setSummaryLoading(false);
    }
  };

  const handleClassify = async () => {
    setClassifyLoading(true);
    setClassifyError(null);
    try {
      const data = await dmsApi.aiClassify(doc._id);
      setClassification(data);
    } catch (err) {
      setClassifyError(err.error || 'Failed to classify document');
    } finally {
      setClassifyLoading(false);
    }
  };

  const handleAsk = async () => {
    if (!question.trim()) return;
    const q = question.trim();
    setQuestion('');
    setAskLoading(true);
    setAskError(null);
    setConversation(prev => [...prev, { role: 'user', content: q }]);
    try {
      const data = await dmsApi.aiAsk(doc._id, q);
      const answer = data.answer || data.response || (typeof data === 'string' ? data : JSON.stringify(data));
      setConversation(prev => [...prev, { role: 'assistant', content: answer }]);
    } catch (err) {
      setAskError(err.error || 'Failed to get answer');
      setConversation(prev => [...prev, { role: 'assistant', content: 'Error: Could not get an answer.' }]);
    } finally {
      setAskLoading(false);
    }
  };

  const handleCompliance = async () => {
    setComplianceLoading(true);
    setComplianceError(null);
    try {
      const data = await dmsApi.aiCompliance(doc._id);
      setComplianceResults(data.results || data.issues || data);
    } catch (err) {
      setComplianceError(err.error || 'Failed to run compliance scan');
    } finally {
      setComplianceLoading(false);
    }
  };

  const handleTranslate = async () => {
    setTranslateLoading(true);
    setTranslateError(null);
    try {
      const data = await dmsApi.aiTranslate(doc._id, targetLang);
      setTranslation(data);
    } catch (err) {
      setTranslateError(err.error || 'Failed to translate document');
    } finally {
      setTranslateLoading(false);
    }
  };

  const handleQAKeyDown = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleAsk();
    }
  };

  return (
    <Card sx={{ bgcolor: MP.surface, border: `1px solid ${MP.border}` }}>
      <CardContent sx={{ p: 2 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
          <AutoAwesomeIcon sx={{ color: MP.green }} />
          <Typography variant="subtitle1" fontWeight={600}>AI Assistant</Typography>
        </Box>

        <Tabs
          value={tab} onChange={(_, v) => setTab(v)}
          variant="scrollable" scrollButtons="auto"
          sx={{ borderBottom: `1px solid ${MP.border}`, mb: 0 }}
        >
          <Tab icon={<AutoAwesomeIcon />} iconPosition="start" label="Summary" sx={{ minHeight: 40, textTransform: 'none', fontSize: '0.8rem' }} />
          <Tab icon={<CategoryIcon />} iconPosition="start" label="Classify" sx={{ minHeight: 40, textTransform: 'none', fontSize: '0.8rem' }} />
          <Tab icon={<QuestionAnswerIcon />} iconPosition="start" label="Q&A" sx={{ minHeight: 40, textTransform: 'none', fontSize: '0.8rem' }} />
          <Tab icon={<SecurityIcon />} iconPosition="start" label="Compliance" sx={{ minHeight: 40, textTransform: 'none', fontSize: '0.8rem' }} />
          <Tab icon={<TranslateIcon />} iconPosition="start" label="Translate" sx={{ minHeight: 40, textTransform: 'none', fontSize: '0.8rem' }} />
        </Tabs>

        {/* Summary Tab */}
        <TabPanel value={tab} index={0}>
          {summaryError && <Alert severity="error" sx={{ mb: 2 }}>{summaryError}</Alert>}
          {summary ? (
            <Box sx={{ bgcolor: MP.deepBlue, border: `1px solid ${MP.border}`, borderRadius: 1, p: 2 }}>
              <Typography variant="body2" sx={{ lineHeight: 1.7, whiteSpace: 'pre-wrap', mb: 2 }}>
                {typeof summary === 'string' ? summary : summary.text || JSON.stringify(summary)}
              </Typography>

              {/* Word count / reading time */}
              <Box sx={{ display: 'flex', gap: 1, mb: 2 }}>
                <Chip size="small" label={`${(typeof summary === 'string' ? summary : (summary.text || '')).split(' ').filter(Boolean).length} words`} variant="outlined" />
                <Chip size="small" label={`~${Math.max(1, Math.ceil((typeof summary === 'string' ? summary : (summary.text || '')).split(' ').filter(Boolean).length / 200))} min read`} variant="outlined" />
              </Box>

              {/* Keywords extraction */}
              <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1 }}>Key themes:</Typography>
              <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap' }}>
                {extractKeywords(typeof summary === 'string' ? summary : (summary.text || '')).map(kw => (
                  <Chip key={kw} label={kw} size="small" sx={{ bgcolor: 'primary.dark', fontSize: '0.65rem' }} />
                ))}
              </Box>
            </Box>
          ) : (
            <Box sx={{ textAlign: 'center', py: 3, color: MP.textSecondary }}>
              <Typography variant="body2" sx={{ mb: 2 }}>No summary generated yet</Typography>
            </Box>
          )}
          <Button
            variant="outlined" startIcon={summaryLoading ? <CircularProgress size={16} color="inherit" /> : <AutoAwesomeIcon />}
            onClick={handleSummarize} disabled={summaryLoading}
            sx={{ mt: 2 }} size="small"
          >
            {summary ? 'Regenerate Summary' : 'Generate Summary'}
          </Button>
        </TabPanel>

        {/* Classify Tab */}
        <TabPanel value={tab} index={1}>
          {classifyError && <Alert severity="error" sx={{ mb: 2 }}>{classifyError}</Alert>}

          {/* Current vs AI suggested classification */}
          {classification && (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2, p: 1.5, bgcolor: 'action.hover', borderRadius: 1 }}>
              <Box>
                <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.5 }}>Current</Typography>
                <ClassificationBadge classification={doc?.classification || 'unclassified'} />
              </Box>
              <Typography variant="body2" color="text.secondary">→</Typography>
              <Box>
                <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.5 }}>AI Suggests</Typography>
                <ClassificationBadge classification={classification?.suggested_classification || classification?.suggestedClassification || 'unclassified'} />
              </Box>
            </Box>
          )}
          <Box sx={{ bgcolor: MP.deepBlue, border: `1px solid ${MP.border}`, borderRadius: 1, p: 2, mb: 2 }}>
            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.5 }}>Current Vault</Typography>
            <Chip label={doc?.vault || 'Unassigned'} size="small" sx={{ bgcolor: `${MP.blue}30`, color: MP.blue, mb: 1 }} />
            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.5, mt: 1 }}>Current Tags</Typography>
            <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap' }}>
              {(doc?.tags || []).length > 0 ? doc.tags.map(tag => (
                <Chip key={tag} label={tag} size="small" variant="outlined" sx={{ fontSize: '0.7rem' }} />
              )) : (
                <Typography variant="caption" color="text.secondary">No tags</Typography>
              )}
            </Box>
          </Box>

          {classification && (
            <Box sx={{ bgcolor: `${MP.green}10`, border: `1px solid ${MP.green}40`, borderRadius: 1, p: 2, mb: 2 }}>
              <Typography variant="caption" sx={{ color: MP.green, fontWeight: 600, display: 'block', mb: 0.5 }}>
                AI Suggestion
              </Typography>
              <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.5 }}>Suggested Vault</Typography>
              <Chip label={classification.suggestedVault || classification.vault || '—'} size="small"
                sx={{ bgcolor: `${MP.green}30`, color: MP.green, mb: 1 }}
              />
              <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.5, mt: 1 }}>Suggested Tags</Typography>
              <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap' }}>
                {(classification.suggestedTags || classification.tags || []).map(tag => (
                  <Chip key={tag} label={tag} size="small" sx={{ bgcolor: `${MP.green}20`, color: MP.green, fontSize: '0.7rem' }} />
                ))}
              </Box>
            </Box>
          )}

          <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', mt: 0 }}>
            <Button
              variant="outlined" startIcon={classifyLoading ? <CircularProgress size={16} color="inherit" /> : <CategoryIcon />}
              onClick={handleClassify} disabled={classifyLoading}
              size="small"
            >
              {classification ? 'Re-classify' : 'Classify Document'}
            </Button>
            {classification && (classification.suggested_classification || classification.suggestedClassification) && (
              <Button
                variant="contained" size="small" color="success"
                onClick={async () => {
                  const suggested = classification.suggested_classification || classification.suggestedClassification;
                  try {
                    await dmsApi.updateDocument(doc._id, { classification: suggested });
                    setClassification(null);
                  } catch { /* silent */ }
                }}
              >
                Apply AI Classification
              </Button>
            )}
          </Box>
        </TabPanel>

        {/* Q&A Tab */}
        <TabPanel value={tab} index={2}>
          {askError && <Alert severity="error" sx={{ mb: 2 }}>{askError}</Alert>}
          <Box sx={{
            bgcolor: MP.deepBlue, border: `1px solid ${MP.border}`, borderRadius: 1,
            maxHeight: 300, overflowY: 'auto', p: 2, mb: 2,
            minHeight: 120,
          }}>
            {conversation.length === 0 ? (
              <Box sx={{ textAlign: 'center', py: 3, color: MP.textSecondary }}>
                <QuestionAnswerIcon sx={{ fontSize: 28, mb: 0.5 }} />
                <Typography variant="body2">Ask questions about this document</Typography>
              </Box>
            ) : (
              conversation.map((msg, idx) => (
                <Box
                  key={idx}
                  sx={{
                    display: 'flex',
                    justifyContent: msg.role === 'user' ? 'flex-end' : 'flex-start',
                    mb: 1.5,
                  }}
                >
                  <Box sx={{
                    maxWidth: '80%',
                    bgcolor: msg.role === 'user' ? `${MP.blue}30` : MP.surface,
                    border: `1px solid ${msg.role === 'user' ? MP.blue : MP.border}`,
                    borderRadius: 1.5, px: 1.5, py: 1,
                  }}>
                    <Typography variant="body2" sx={{ fontSize: '0.8rem', lineHeight: 1.6, whiteSpace: 'pre-wrap' }}>
                      {msg.content}
                    </Typography>
                  </Box>
                </Box>
              ))
            )}
            {askLoading && (
              <Box sx={{ display: 'flex', gap: 1, alignItems: 'center', color: MP.textSecondary }}>
                <CircularProgress size={14} />
                <Typography variant="caption">Thinking...</Typography>
              </Box>
            )}
          </Box>

          {/* Suggested questions when conversation is empty */}
          {conversation.length === 0 && (
            <Box sx={{ mt: 0, mb: 1 }}>
              <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1 }}>Suggested questions:</Typography>
              {[
                'What are the key terms and conditions?',
                'Who are the parties involved?',
                'What are the main risks or obligations?',
                'When does this expire or need renewal?',
              ].map(q => (
                <Chip key={q} label={q} size="small" variant="outlined" onClick={() => setQuestion(q)}
                  sx={{ mr: 0.5, mb: 0.5, cursor: 'pointer', fontSize: '0.65rem' }} />
              ))}
            </Box>
          )}

          <Box sx={{ display: 'flex', gap: 1 }}>
            <TextField
              fullWidth size="small" placeholder="Ask about this document..."
              value={question} onChange={(e) => setQuestion(e.target.value)}
              onKeyDown={handleQAKeyDown}
              disabled={askLoading}
              sx={{ '& .MuiOutlinedInput-root': { bgcolor: MP.deepBlue } }}
            />
            <IconButton
              color="primary" onClick={handleAsk}
              disabled={!question.trim() || askLoading}
              sx={{ bgcolor: MP.blue, color: MP.white, '&:hover': { bgcolor: `${MP.blue}CC` }, '&.Mui-disabled': { bgcolor: MP.surface } }}
            >
              <SendIcon />
            </IconButton>
          </Box>
        </TabPanel>

        {/* Compliance Tab */}
        <TabPanel value={tab} index={3}>
          {complianceError && <Alert severity="error" sx={{ mb: 2 }}>{complianceError}</Alert>}

          {complianceResults ? (
            <Box>
              {Array.isArray(complianceResults) && complianceResults.length > 0 ? (
                <Box>
                  {/* Severity summary chips */}
                  <Box sx={{ display: 'flex', gap: 1, mb: 2, flexWrap: 'wrap' }}>
                    {['critical', 'warning', 'info'].map(severity => {
                      const count = complianceResults.filter(r =>
                        r.severity === severity ||
                        (severity === 'warning' && r.type === 'warning') ||
                        (severity === 'info' && r.type === 'info')
                      ).length;
                      if (count === 0) return null;
                      return (
                        <Chip key={severity} size="small"
                          label={`${count} ${severity}`}
                          color={severity === 'critical' ? 'error' : severity === 'warning' ? 'warning' : 'info'}
                        />
                      );
                    })}
                  </Box>

                  {/* Issues list with Alert */}
                  {complianceResults.map((issue, idx) => (
                    <Alert key={idx} severity={issue.severity === 'critical' ? 'error' : issue.severity || 'info'} sx={{ mb: 1 }}>
                      <Typography variant="body2" fontWeight={600}>{issue.issue || issue.message || issue.title || issue.rule || 'Issue'}</Typography>
                      {(issue.recommendation || issue.description || issue.details) && (
                        <Typography variant="caption">{issue.recommendation || issue.description || issue.details}</Typography>
                      )}
                    </Alert>
                  ))}
                </Box>
              ) : (
                <Box sx={{ textAlign: 'center', py: 3, bgcolor: `${MP.green}10`, borderRadius: 1, border: `1px solid ${MP.green}40` }}>
                  <CheckCircleIcon sx={{ color: MP.green, fontSize: 36, mb: 1 }} />
                  <Typography variant="body2" sx={{ color: MP.green }}>
                    No compliance issues found
                  </Typography>
                </Box>
              )}
            </Box>
          ) : (
            <Box sx={{ textAlign: 'center', py: 3, color: MP.textSecondary }}>
              <SecurityIcon sx={{ fontSize: 36, mb: 1 }} />
              <Typography variant="body2">Run a compliance scan to check for issues</Typography>
            </Box>
          )}

          <Button
            variant="outlined" startIcon={complianceLoading ? <CircularProgress size={16} color="inherit" /> : <SecurityIcon />}
            onClick={handleCompliance} disabled={complianceLoading}
            sx={{ mt: 2 }} size="small"
          >
            {complianceResults ? 'Re-scan' : 'Run Compliance Scan'}
          </Button>
        </TabPanel>

        {/* Translate Tab */}
        <TabPanel value={tab} index={4}>
          {translateError && <Alert severity="error" sx={{ mb: 2 }}>{translateError}</Alert>}

          <Box sx={{ display: 'flex', gap: 2, alignItems: 'center', mb: 2 }}>
            <FormControl size="small" sx={{ minWidth: 180 }}>
              <InputLabel>Target Language</InputLabel>
              <Select value={targetLang} onChange={(e) => setTargetLang(e.target.value)} label="Target Language">
                {LANGUAGES.map(lang => (
                  <MenuItem key={lang.code} value={lang.code}>{lang.label}</MenuItem>
                ))}
              </Select>
            </FormControl>
            <Button
              variant="outlined" startIcon={translateLoading ? <CircularProgress size={16} color="inherit" /> : <TranslateIcon />}
              onClick={handleTranslate} disabled={translateLoading}
              size="small"
            >
              Translate
            </Button>
          </Box>

          {translateLoading && (
            <Box sx={{ textAlign: 'center', py: 3 }}>
              <CircularProgress size={24} />
              <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1 }}>
                Translating document...
              </Typography>
            </Box>
          )}

          {translation && !translateLoading && (
            <Box sx={{ bgcolor: MP.deepBlue, border: `1px solid ${MP.border}`, borderRadius: 1, p: 2 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                <TranslateIcon sx={{ fontSize: 16, color: MP.green }} />
                <Typography variant="caption" sx={{ color: MP.green, fontWeight: 600 }}>
                  Translated to {LANGUAGES.find(l => l.code === targetLang)?.label || targetLang}
                </Typography>
              </Box>
              <Typography variant="body2" sx={{ lineHeight: 1.7, whiteSpace: 'pre-wrap' }}>
                {translation.translatedText || translation.text || translation.content || (typeof translation === 'string' ? translation : JSON.stringify(translation))}
              </Typography>
              {translation.downloadUrl && (
                <Button
                  variant="text" size="small" sx={{ mt: 1 }}
                  onClick={() => window.open(translation.downloadUrl, '_blank')}
                >
                  Download Translated File
                </Button>
              )}
            </Box>
          )}

          {!translation && !translateLoading && (
            <Box sx={{ textAlign: 'center', py: 3, color: MP.textSecondary }}>
              <TranslateIcon sx={{ fontSize: 36, mb: 1 }} />
              <Typography variant="body2">Select a language and translate</Typography>
            </Box>
          )}
        </TabPanel>
      </CardContent>
    </Card>
  );
}
